import { useApolloClient } from '@apollo/client';
import { debounce } from '@zg-rentals/zdash';
import { useContext, useEffect, useRef, useState } from 'react';
import type { PropertyFilterHook_GetPropertySearchResultQuery } from '../types/GraphqlTypes';
import {
  PropertyFilterHook_GetPropertySearchResultDocument,
  usePropertyFilterHook_GetPropertyAddressLazyQuery,
  usePropertyFilterHook_GetPropertySearchResultQuery,
} from '../types/GraphqlTypes';
import { InboxPageContext } from '../contexts/InboxPageContextProvider';
import { logApolloError } from '../utils/errorUtils';

export type PropertyListingItem = {
  id: string;
  label: string;
  value: string;
};

export type UpdatePathWithListingAlias = (listingAlias: string) => void;

type UsePropertyFiltersArgs = {
  defaultListingAliasOnLoad?: string;
  resetPropertyFilter?: () => void;
  updatePathWithListingAlias: UpdatePathWithListingAlias;
  includeMultifamily?: boolean;
  includeSingleFamily?: boolean;
};

// Filter special characters in property search input, but include characters such as - /
// since they can be found in the property name
const filterSpecialCharacters = (str: string) => str.trim().replace(/[^a-zA-Z0-9-/ ]/g, '');

const formatPropertyListings = (
  listings: PropertyFilterHook_GetPropertySearchResultQuery['ZRM_getPropertySearchResult']['listings'],
): Array<PropertyListingItem> => {
  return listings.map((listing) => {
    const { address, isMultifamily, listingAlias, name } = listing;
    const { city = '', state = '', street = '', unit = '', zip = '' } = address || {};
    const cityStateZip = `${city?.trim()}, ${state} ${zip}`;
    const namePrefix = isMultifamily && name ? name.trim() : '';
    const unitWithCityStateZip = unit ? `#${unit} ${cityStateZip}` : cityStateZip;
    const streetLabel = namePrefix
      ? `${namePrefix}\n${street?.trim()} ${unitWithCityStateZip}`
      : `${street?.trim()} ${unitWithCityStateZip}`;
    const streetWithUnitValue = unit ? `${street} ${unit}` : street;
    const value = `${namePrefix} ${streetWithUnitValue} ${city}`.trim();

    return {
      id: listingAlias || value,
      value,
      label: streetLabel,
    };
  });
};

const ERROR_FETCHING_PROPERTY_LISTINGS =
  'Sorry, we are having trouble getting property listings to search by. Please refresh the page and try again.';
const ERROR_FETCHING_PROPERTY_ADDRESS = 'Sorry, we were unable to filter by that property. Please try a new search.';

/**
 * @param defaultListingAliasOnLoad listingAlias to set on page load
 * @param resetPropertyFilter Function to remove selected listingAlias in the URL if user input is empty or API call errors
 * @param updatePathWithListingAlias Function to update the URL with selected listingAlias
 * @param includeMultifamily Whether to include multifamily listings
 * @param includeSingleFamily Whether to include single family listings
 * @description Helper functions to handle filtering a list of properties based on user input or selection
 * and updating the URL with selected property listingAlias
 */
export default ({
  defaultListingAliasOnLoad,
  resetPropertyFilter,
  updatePathWithListingAlias,
  includeMultifamily = true,
  includeSingleFamily = true,
}: UsePropertyFiltersArgs) => {
  const apolloClient = useApolloClient();
  const { listingAlias } = useContext(InboxPageContext);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTextReason, setSearchTextReason] = useState('');

  const { data: propertyListingData, fetchMore } = usePropertyFilterHook_GetPropertySearchResultQuery({
    fetchPolicy: 'cache-first',
    variables: {
      searchText: debouncedSearchText,
      startKeyExclusive: '',
      includeMultifamily,
      includeSingleFamily,
    },
    onError(err) {
      setErrorMessage(ERROR_FETCHING_PROPERTY_LISTINGS);

      logApolloError(err, {
        details: 'PropertyFilterHook - Error fetching property listings',
        listingAlias,
        searchText: debouncedSearchText,
      });
    },
  });

  // Highlight the search text in the property listing results. Include special characters such as - # , /
  const highlightPatternRegex = new RegExp(searchText.trim().replace(/[^a-zA-Z0-9-#,/ ]/g, ''), 'i');
  const { listings, pager } = propertyListingData?.ZRM_getPropertySearchResult || {};
  const propertyListings = formatPropertyListings(listings || []);
  const hasMoreToPaginate = pager?.hasMore || false;

  const [getPropertyAddressForListingAlias, { data: propertyAddressData, loading: isPropertyAddressDataLoading }] =
    usePropertyFilterHook_GetPropertyAddressLazyQuery({
      onError(err) {
        if (resetPropertyFilter) {
          resetPropertyFilter();
        }

        setErrorMessage(ERROR_FETCHING_PROPERTY_ADDRESS);
        logApolloError(err, {
          details: 'PropertyFilterHook - Error fetching property address for listing',
          listingAlias,
        });
      },
    });

  // Look up property address based on listingAlias from the url
  // and display it in the property address input
  const { street = '', unit = '', city = '', state = '', zip = '' } = propertyAddressData?.ZRM_getPropertyAddress || {};
  const streetWithUnitDisplay = unit ? `${street} #${unit}` : street;
  const streetDisplay = streetWithUnitDisplay ? `${streetWithUnitDisplay} ${city}, ${state} ${zip}`.trim() : '';
  const shouldShowSkeleton = isPropertyAddressDataLoading && streetDisplay === '';

  useEffect(() => {
    if (streetDisplay) {
      setSearchText(streetDisplay);
    }
  }, [streetDisplay]);

  useEffect(() => {
    if (defaultListingAliasOnLoad) {
      getPropertyAddressForListingAlias({
        variables: {
          listingAlias: defaultListingAliasOnLoad,
        },
        returnPartialData: true,
      });
    }
  }, []);

  const debouncedSearch = useRef(
    debounce(async (inputSearchText: string) => {
      const filteredSearchText = filterSpecialCharacters(inputSearchText);
      await apolloClient.query({
        query: PropertyFilterHook_GetPropertySearchResultDocument,
        variables: {
          searchText: filteredSearchText,
          startKeyExclusive: '',
          includeMultifamily,
          includeSingleFamily,
        },
        fetchPolicy: 'cache-first',
      });

      setDebouncedSearchText(filteredSearchText);
    }, 500),
  ).current;

  useEffect(() => {
    // When the component unmounts, the search request could
    // still be in progress, so we cancel the debounced function
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSelectProperty = (selectedPropertyListingItem: PropertyListingItem) => {
    updatePathWithListingAlias(selectedPropertyListingItem.id);
  };

  const handleChange = (inputSearchText = '', { reason = '' }) => {
    setSearchText(inputSearchText);
    setSearchTextReason(reason);

    if (errorMessage) {
      setErrorMessage('');
    }

    if (!inputSearchText && resetPropertyFilter) {
      resetPropertyFilter();
    }

    // We only run the search to filter properties as the user types,
    // and not when a property is selected
    if (reason === 'freeform' || reason.includes('clear')) {
      debouncedSearch(inputSearchText);
    }
  };

  const handleScroll = (isLastPropertyVisible: boolean) => {
    if (isLastPropertyVisible && hasMoreToPaginate) {
      fetchMore({
        variables: {
          searchText: searchTextReason === 'freeform' ? filterSpecialCharacters(searchText) : '',
          startKeyExclusive: pager?.endKey,
        },
      });
    }
  };

  return {
    errorMessage,
    handleChange,
    handleScroll,
    handleSelectProperty,
    hasMoreToPaginate,
    highlightPatternRegex,
    propertyListings,
    searchText,
    shouldShowSkeleton,
  };
};
